// ContactComp.tsx
// Modernized SupaRecords Contact Component
// NOTE: Replace the EmailJS service/template/public keys with your own values.

import React, { useState } from "react";
import {
  LoadingOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  WhatsAppOutlined,
  SendOutlined,
} from "@ant-design/icons";
import Slide from "react-reveal/Slide";
import emailjs from "@emailjs/browser";
import { contactDetails } from "../../../utils/appconst";

type FormData = {
  fullName: string;
  company: string;
  businessType: string;
  phone: string;
  email: string;
  employees: string;
  subject: string;
  message: string;
};

const businessTypes = [
  "Retail Store",
  "Supermarket",
  "Restaurant",
  "Hotel",
  "Hospital",
  "Pharmacy",
  "Warehouse",
  "Manufacturing",
  "Wholesale",
  "Electronics",
  "Construction",
  "Other",
];

const ContactComp: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState<FormData>({
    fullName: "",
    company: "",
    businessType: "",
    phone: "",
    email: "",
    employees: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const resetForm = () =>
    setForm({
      fullName: "",
      company: "",
      businessType: "",
      phone: "",
      email: "",
      employees: "",
      subject: "",
      message: "",
    });

  const sendReply = async () => {
    try {
      await emailjs.send(
        "service_Lamphill",
        "template_sender",
        { email: form.email },
        { publicKey: "bPhK6vjI5kNa_T6Ot" }
      );
    } catch {}
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await emailjs.send(
        "service_Lamphill",
        "template_Owner",
        {
          fullName: form.fullName,
          company: form.company,
          businessType: form.businessType,
          phoneNumber: form.phone,
          email: form.email,
          employees: form.employees,
          subject: form.subject,
          message: form.message,
        },
        {
          publicKey: "bPhK6vjI5kNa_T6Ot",
        }
      );

      await sendReply();

      alert("Thank you! Your message has been sent.");
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Unable to send your message. Please try again.");
    }

    setLoading(false);
  };

  const info = [
    {
      icon: <PhoneOutlined />,
      title: "Call Us",
      value: contactDetails.phoneNumberContact,
      link: `tel:${contactDetails.phoneNumberContact}`,
    },
    {
      icon: <MailOutlined />,
      title: "Email",
      value: contactDetails.emailAddress,
      link: `mailto:${contactDetails.emailAddress}`,
    },
    {
      icon: <WhatsAppOutlined />,
      title: "WhatsApp",
      value: "Chat with our team",
      link: `https://wa.me/${contactDetails.whatsAppContact}?text=${contactDetails.whatAppLeadingMessage}`,
    },
    {
      icon: <EnvironmentOutlined />,
      title: "Office",
      value: contactDetails.address,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-slate-50 py-24">
      <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-red-100 blur-[140px]" />
      <div className="absolute right-0 bottom-0 h-[420px] w-[420px] rounded-full bg-orange-100 blur-[150px]" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Slide left>
            <div>
              <div className="inline-flex rounded-full bg-red-50 px-5 py-2 text-red-600 font-semibold">
                Contact Us
              </div>

              <h2 className="mt-6 text-5xl font-black text-slate-900">
                Let's Talk About
                <span className="block bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">
                  Your Business
                </span>
              </h2>

              <p className="mt-6 text-lg leading-8 text-gray-600">
                Tell us about your business and we'll help you choose the right
                SupaRecords solution.
              </p>

              {/* <div className="mt-10 grid gap-5">
                {info.map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600 to-orange-500 text-white text-xl">
                      {item.icon}
                    </div>

                    <div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>

                      {item.link ? (
                        <a
                          href={item.link}
                          target={item.title === "WhatsApp" ? "_blank" : undefined}
                          rel="noreferrer"
                          className="text-gray-600 hover:text-red-600"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-gray-600">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div> */}

              <img
                src={`${process.env.PUBLIC_URL}/images/contact/lets_talk_b.jpg`}
                alt="Contact"
                className="mt-10 rounded-[32px] shadow-xl"
              />
            </div>
          </Slide>

          <Slide right>
            <form
              onSubmit={submit}
              className="rounded-[36px] border border-gray-200 bg-white p-10 shadow-xl"
            >
              <h3 className="text-3xl font-black text-slate-900">
                Request a Demo
              </h3>

              <p className="mt-3 text-gray-600">
                Complete the form below and our team will contact you shortly.
              </p>

              <div className="mt-8 grid md:grid-cols-2 gap-5">
                <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Full Name" required className="rounded-2xl border p-4 outline-none focus:border-red-500"/>
                <input name="company" value={form.company} onChange={handleChange} placeholder="Company Name" required className="rounded-2xl border p-4 outline-none focus:border-red-500"/>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" required className="rounded-2xl border p-4 outline-none focus:border-red-500"/>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address" required className="rounded-2xl border p-4 outline-none focus:border-red-500"/>
                <select name="businessType" value={form.businessType} onChange={handleChange} required className="rounded-2xl border p-4 outline-none focus:border-red-500">
                  <option value="">Business Type</option>
                  {businessTypes.map((b)=><option key={b}>{b}</option>)}
                </select>
                <input name="employees" value={form.employees} onChange={handleChange} placeholder="Employees (Optional)" className="rounded-2xl border p-4 outline-none focus:border-red-500"/>
              </div>

              <input name="subject" value={form.subject} onChange={handleChange} placeholder="Subject" required className="mt-5 w-full rounded-2xl border p-4 outline-none focus:border-red-500"/>

              <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your business..." rows={6} required className="mt-5 w-full rounded-2xl border p-4 outline-none focus:border-red-500"/>

              <button
                disabled={loading}
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-red-600 to-orange-500 px-8 py-5 font-semibold text-white transition hover:scale-[1.01]"
              >
                {loading ? <LoadingOutlined /> : <><SendOutlined /> Send Message</>}
              </button>
            </form>
          </Slide>
        </div>
      </div>
    </section>
  );
};

export default ContactComp;
